/* eslint-disable react/no-unstable-nested-components */
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ClearIcon from '@mui/icons-material/Clear';
import {
  StyledContainerItems,
  StyledTitleSideBar,
  StyledContainerFooterSideBar
} from '../../../components/commons/SideBarComponents/sideBarComponentsStyles';
import Strings from '../../../i18n';
import { SideBarSwitchWithLabel } from '../../../components/commons/SideBarComponents/SwitchWithLabel/sideBarSwitchWithLabel';
import { SideBarSimpleMethodItem } from '../../../components/commons/SideBarComponents/SimpleMethodItem/sideBarSimpleMethodButtonItem';
import {
  useGetAllProductNamesMutation,
  useGetFirmwareVersionMutation
} from '../../../services/device/deviceService';
import { SelectItemMethod } from '../../../components/commons/SideBarComponents/SelectItemMethod/selectItemMethod';
import { StyledClearButton, StyledIconClear } from './deviceFiltersStyle';
import { SideBarEditablePropertyItem } from '../../../components/commons/SideBarComponents/EditablePropertyItem/sideBarEditablePropertyItem';
import { ControllerConnectionStatesEnum } from '../../../constants/enums/deviceEnums';

interface DeviceFiltersSideBarProps {
  reloadTable: () => void;
  setOpenRightSideBar: (value: boolean) => void;
  openRightSideBar: boolean;
}
export const DeviceFiltersSideBar = ({
  reloadTable,
  setOpenRightSideBar,
  openRightSideBar
}: DeviceFiltersSideBarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const [getProducts, { data: dataProducts, isSuccess: isSuccessGetProduct }] =
    useGetAllProductNamesMutation();
  const [
    getFirmwareVersion,
    { data: dataVersion, isSuccess: isSuccessVersion }
  ] = useGetFirmwareVersionMutation();
  const showUnassignedDevicesByUrl = queryParams.get('showUnassignedDevices');
  const showPendingFotaDevicesByUrl = queryParams.get('showPendingFotaDevices');
  const filterByModel = queryParams.get('filterByModel');
  const filterByVersion = queryParams.get('filterByVersion');
  const filterByStatus = queryParams.get('filterByStatus');
  const filterByKeyword = queryParams.get('filterByKeyword');
  const filterByUser = queryParams.get('filterByUser');
  const filterByLocationStatus = queryParams.get('filterByLocationStatus');
  const filterByLocationValues = [
    { id: 'all', name: 'All' },
    { id: 'unassignedOnly', name: 'Unnasigned Only' },
    { id: 'assignedOnly', name: 'Assigned Only' }
  ];
  const [selectedModel, setSelectedModel] = useState<string>(
    filterByModel || ''
  );
  const [filters, setFilters] = useState({
    showOnlyUnassignedDevices: Boolean(showUnassignedDevicesByUrl),
    filterByModel: filterByModel || '',
    showOnlyPendingFotaDevices: Boolean(showPendingFotaDevicesByUrl),
    filterByVersion: filterByVersion || '',
    filterByStatus: filterByStatus || '',
    filterByKeyword: filterByKeyword || '',
    filterByLocationStatus: filterByLocationStatus || '',
    filterByUser: filterByUser || ''
  });
  const closeSideBar = () => {
    setOpenRightSideBar(false);
  };
  const statusOptions = [
    { id: ControllerConnectionStatesEnum.CONNECTED, name: 'Online' },
    { id: ControllerConnectionStatesEnum.DISCONNECTED, name: 'Offline' }
  ];
  const applyFilters = () => {
    const filterConfig = [
      {
        key: 'showUnassignedDevices',
        value: filters.showOnlyUnassignedDevices,
        useSelectedModel: false
      },
      {
        key: 'filterByModel',
        value: filters.filterByModel,
        useSelectedModel: true
      },
      {
        key: 'showPendingFotaDevices',
        value: filters.showOnlyPendingFotaDevices,
        useSelectedModel: false
      },
      {
        key: 'filterByVersion',
        value: filters.filterByVersion,
        useSelectedModel: false
      },
      {
        key: 'filterByStatus',
        value: filters.filterByStatus,
        useSelectedModel: false
      },
      {
        key: 'filterByKeyword',
        value: filters.filterByKeyword,
        useSelectedModel: false
      },
      {
        key: 'filterByLocationStatus',
        value: filters.filterByLocationStatus,
        useSelectedModel: false
      },
      {
        key: 'filterByUser',
        value: filters.filterByUser,
        useSelectedModel: false
      }
    ];

    filterConfig.forEach(({ key, value, useSelectedModel }) => {
      if (value && (!useSelectedModel || selectedModel)) {
        queryParams.set(key, useSelectedModel ? selectedModel : String(value));
      } else {
        queryParams.delete(key);
      }
    });

    navigate(`?${queryParams.toString()}`);
    reloadTable();
    closeSideBar();
  };

  const removeFilters = () => {
    const newUrl = window.location.pathname;
    navigate(newUrl);
    reloadTable();
    closeSideBar();
  };

  const clearFilter = (filterName: string) => {
    setFilters({
      ...filters,
      [filterName]: filterName === 'filterByModel' ? '' : false
    });
    if (filterName === 'filterByModel') {
      setSelectedModel('');
    }
  };

  const FilterWithClearButton = ({
    label,
    filterName,
    children
  }: {
    label: string;
    filterName: string;
    children: React.ReactNode;
  }) => (
    <div className="position-relative">
      <StyledClearButton>
        <span />
        {((filterName in filters &&
          filters[filterName as keyof typeof filters]) ||
          (filterName === 'filterByModel' && selectedModel)) && (
          <StyledIconClear
            type="button"
            onClick={() => clearFilter(filterName)}
          >
            Clear <ClearIcon fontSize="small" />
          </StyledIconClear>
        )}
      </StyledClearButton>
      {children}
    </div>
  );

  useEffect(() => {
    getFirmwareVersion('');
    getProducts('');
  }, []);

  useEffect(() => {
    if (isSuccessGetProduct && dataProducts) {
      setFilters({
        ...filters,
        filterByModel:
          dataProducts
            .find((product) => product.name === selectedModel)
            ?.id?.toString() ?? ''
      });
    }
  }, [isSuccessGetProduct]);

  return (
    <>
      <StyledContainerItems>
        <StyledTitleSideBar>{Strings.common.filters}</StyledTitleSideBar>
        <SideBarSwitchWithLabel
          className="my-2"
          label="Show pending FOTA devices"
          checked={filters.showOnlyPendingFotaDevices}
          onChange={() =>
            setFilters({
              ...filters,
              showOnlyPendingFotaDevices: !filters.showOnlyPendingFotaDevices
            })
          }
        />
        <SideBarEditablePropertyItem
          className="my-0"
          item={{
            label: Strings.devices.filterByKeyword,
            type: 'inputText',
            currentValue: filters.filterByKeyword,
            placeholder: '',
            onChange: (
              event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
            ) => {
              setFilters({
                ...filters,
                filterByKeyword: event.target.value
              });
            }
          }}
        />
        <SideBarEditablePropertyItem
          className="my-0"
          item={{
            label: Strings.devices.filterByUser,
            type: 'inputText',
            currentValue: filters.filterByUser,
            placeholder: '',
            onChange: (
              event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
            ) => {
              setFilters({
                ...filters,
                filterByUser: event.target.value
              });
            }
          }}
        />
        <SelectItemMethod
          className="m-0 mb-2"
          item={{
            label: Strings.devices.showDevices,
            currentValue: filters.filterByLocationStatus ?? undefined,
            options: filterByLocationValues,
            onChange: (
              event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
            ) => {
              setFilters({
                ...filters,
                filterByLocationStatus: event.target.value
              });
            },
            placeholder: Strings.devices.productType
          }}
        />
        {dataProducts && (
          <FilterWithClearButton
            label={Strings.devices.model}
            filterName="filterByModel"
          >
            <SelectItemMethod
              className="m-0"
              item={{
                label: Strings.devices.model,
                currentValue: filters.filterByModel ?? undefined,
                options: dataProducts || [],
                onChange: (
                  event: React.ChangeEvent<
                    HTMLInputElement | HTMLTextAreaElement
                  >
                ) => {
                  setFilters({
                    ...filters,
                    filterByModel: event.target.value
                  });
                  setSelectedModel(
                    dataProducts.find(
                      (product) => product.id === +event.target.value
                    )?.name ?? ''
                  );
                },
                placeholder: Strings.devices.productType
              }}
            />
          </FilterWithClearButton>
        )}

        {dataVersion && dataVersion.length && (
          <FilterWithClearButton
            label={Strings.devices.version}
            filterName="filterByVersion"
          >
            <SelectItemMethod
              className="my-2"
              item={{
                label: Strings.devices.version,
                currentValue: filters.filterByVersion ?? undefined,
                options: (dataVersion || [])
                  .map((version) => ({ name: version, id: version }))
                  .sort((a, b) => {
                    const [majorA, minorA] = a.name.split('.').map(Number);
                    const [majorB, minorB] = b.name.split('.').map(Number);
                    return majorB - majorA || minorB - minorA;
                  }),
                onChange: (
                  event: React.ChangeEvent<
                    HTMLInputElement | HTMLTextAreaElement
                  >
                ) => {
                  setFilters({
                    ...filters,
                    filterByVersion: event.target.value
                  });
                },
                placeholder: Strings.devices.version
              }}
            />
          </FilterWithClearButton>
        )}
        <FilterWithClearButton
          label={Strings.devices.status}
          filterName="filterByStatus"
        >
          <SelectItemMethod
            className="m-0"
            item={{
              label: Strings.devices.status,
              currentValue: filters.filterByStatus ?? undefined,
              options: statusOptions || [],
              onChange: (
                event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => {
                setFilters({
                  ...filters,
                  filterByStatus: event.target.value
                });
              },
              placeholder: Strings.devices.status
            }}
          />
        </FilterWithClearButton>
        <SideBarSimpleMethodItem
          name={Strings.common.removeFilters}
          action={() => removeFilters()}
          successAction={false}
          textSuccess="success"
        />
      </StyledContainerItems>
      <StyledContainerFooterSideBar>
        <button
          type="button"
          className="btn-cancel"
          onClick={() => closeSideBar()}
        >
          {Strings.common.cancel}
        </button>
        <button
          type="button"
          className="btn-save"
          onClick={() => {
            applyFilters();
          }}
        >
          {Strings.common.save}
        </button>
      </StyledContainerFooterSideBar>
    </>
  );
};
